import React, { useRef, useEffect, useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: visible;
`;

const Canvas = styled.canvas`
  cursor: crosshair;
  border-radius: 50%;
`;

const HoverPreview = styled.div`
  position: absolute;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  transform: translate(-50%, -50%);
  z-index: 10;
`;

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 1rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
`;

const Slider = styled.input`
  width: 12rem;
  cursor: pointer;
  accent-color: ${(props) => props.accent || '#007bff'};
`;

const ColorWheel = ({ size = 200, onColorSelect }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  const [hoverColor, setHoverColor] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const [lightness, setLightness] = useState(50);
  const [globalSaturation, setGlobalSaturation] = useState(100);

  const drawColorWheel = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    // Optional Hi-DPI support (keeps drawing crisp)
    const dpr = window.devicePixelRatio || 1;
    // Only set up once per size change
    if (canvas.dataset.dprApplied !== String(dpr)) {
      canvas.width = Math.floor(size * dpr);
      canvas.height = Math.floor(size * dpr);
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;
      canvas.dataset.dprApplied = String(dpr);
    }

    const ctx = canvas.getContext('2d');
    // Work in device pixels for perfect edges
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2;
    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const dx = x - centerX;
        const dy = y - centerY;
        const distance = Math.hypot(dx, dy);
        const idx = (y * width + x) * 4;

        if (distance <= radius) {
          const hue = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;
          const saturation =
            Math.min(distance / radius, 1) * (globalSaturation / 100);
          const light = lightness;

          const c = (1 - Math.abs((2 * light) / 100 - 1)) * saturation;
          const xVal = c * (1 - Math.abs(((hue / 60) % 2) - 1));
          const m = light / 100 - c / 2;
          let r1 = 0;
          let g1 = 0;
          let b1 = 0;

          if (hue < 60) {
            [r1, g1, b1] = [c, xVal, 0];
          } else if (hue < 120) {
            [r1, g1, b1] = [xVal, c, 0];
          } else if (hue < 180) {
            [r1, g1, b1] = [0, c, xVal];
          } else if (hue < 240) {
            [r1, g1, b1] = [0, xVal, c];
          } else if (hue < 300) {
            [r1, g1, b1] = [xVal, 0, c];
          } else {
            [r1, g1, b1] = [c, 0, xVal];
          }

          data[idx] = Math.round((r1 + m) * 255);
          data[idx + 1] = Math.round((g1 + m) * 255);
          data[idx + 2] = Math.round((b1 + m) * 255);
          data[idx + 3] = 255;
        } else {
          data[idx + 3] = 0;
        }
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }, [size, lightness, globalSaturation]);

  useEffect(() => {
    drawColorWheel();
  }, [drawColorWheel]);

  const pickPixel = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }
    const ctx = canvas.getContext('2d');

    const canvasRect = canvas.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    // Mouse position relative to canvas (CSS pixels)
    const cx = event.clientX - canvasRect.left;
    const cy = event.clientY - canvasRect.top;

    // Map CSS pixels -> canvas backing pixels
    const scaleX = canvas.width / canvas.clientWidth;
    const scaleY = canvas.height / canvas.clientHeight;
    const px = Math.floor(cx * scaleX);
    const py = Math.floor(cy * scaleY);

    const [r, g, b, a] = ctx.getImageData(px, py, 1, 1).data;

    // Position preview relative to the container
    const containerX = event.clientX - containerRect.left;
    const containerY = event.clientY - containerRect.top;

    return {
      color: a === 0 ? null : { r, g, b },
      containerPos: { x: containerX, y: containerY },
    };
  };

  const handleClick = (event) => {
    const res = pickPixel(event);
    if (!res?.color) {
      return;
    }
    if (onColorSelect) {
      onColorSelect(res.color);
    }
  };

  const handleMouseMove = (event) => {
    const res = pickPixel(event);
    if (!res) {
      return;
    }
    setHoverPos({
      x: res.containerPos.x,
      y: res.containerPos.y,
    });
    setHoverColor(res.color); // null if outside circle
  };

  const handleMouseLeave = () => setHoverColor(null);

  return (
    <Container ref={containerRef}>
      <Canvas
        ref={canvasRef}
        width={size} /* initial; drawColorWheel will set HiDPI sizes */
        height={size}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />

      {hoverColor && (
        <HoverPreview
          style={{
            left: hoverPos.x,
            top: hoverPos.y,
            backgroundColor: `rgb(${hoverColor.r}, ${hoverColor.g}, ${hoverColor.b})`,
          }}
        />
      )}

      <SliderContainer>
        <Label htmlFor="lightness">Brightness: {lightness}%</Label>
        <Slider
          id="lightness"
          type="range"
          min="0"
          max="100"
          value={lightness}
          onChange={(e) => setLightness(Number(e.target.value))}
          accent="#2f80ed"
        />
      </SliderContainer>

      <SliderContainer>
        <Label htmlFor="saturation">Saturation: {globalSaturation}%</Label>
        <Slider
          id="saturation"
          type="range"
          min="0"
          max="100"
          value={globalSaturation}
          onChange={(e) => setGlobalSaturation(Number(e.target.value))}
          accent="#27ae60"
        />
      </SliderContainer>
    </Container>
  );
};

ColorWheel.propTypes = {
  onColorSelect: PropTypes.func,
  size: PropTypes.number,
};

export default ColorWheel;
